/**
 * API-Client für Bulk-Channel-Texte (Produktliste → Wizard → Review-Queue).
 *
 * - Rezepte je Channel (Vorlage/Grounding/Settings), Sample-Preview an 1 Produkt
 * - Bulk-Runs (N Produkte × M Channels): Estimate → Start → Polling
 * - Review-Queue (Suggestions): Filter, Übernehmen/Verwerfen, Retry fehlgeschlagener Paare
 *
 * Verträge eingefroren — siehe docs/superpowers/plans/2026-07-06-bulk-channel-content-backend.md
 * (Task 13) bzw. docs/superpowers/plans/2026-07-06-bulk-channel-content-frontend.md (API-Verträge).
 * Typen: `channel-content-bulk-types.ts`, Feld-Rezepte: `channel-content-field-recipes.ts` (ausgelagert wegen 200-Zeilen-Limit).
 */

import apiClient from '../api-client'
import type {
  ApplyResult,
  BulkAttributeCode,
  ChannelContentRun,
  ChannelContentSuggestion,
  ChannelFieldSelection,
  ChannelRecipe,
  EstimateResult,
  PreviewResult,
  PushError,
  SuggestionRoute,
  SuggestionStatus,
} from './channel-content-bulk-types'

export type {
  ApplyOutcome,
  ApplyOutcomeChannelDelta,
  ApplyResult,
  BulkAttributeCode,
  ChannelContentRun,
  ChannelContentSuggestion,
  ChannelFieldRecipe,
  ChannelFieldSelection,
  ChannelRecipe,
  EstimateResult,
  PreviewFieldResult,
  PreviewResult,
  PushError,
  RunStatus,
  SuggestionRoute,
  SuggestionStatus,
} from './channel-content-bulk-types'

const BASE = '/api/v1'

/** Baut einen Query-String aus gesetzten (nicht leeren) Filtern. */
export function buildQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      usp.set(key, String(value))
    }
  }
  const qs = usp.toString()
  return qs ? `?${qs}` : ''
}

/** Rezepte des Workspace, optional auf channel_ids gefiltert. */
export async function getRecipes(
  channelIds?: string[],
): Promise<{ success: boolean; recipes: ChannelRecipe[] }> {
  const qs = channelIds && channelIds.length ? buildQuery({ channel_ids: channelIds.join(',') }) : ''
  const res = await apiClient.get(`${BASE}/channel-content/recipes${qs}`)
  return res.data
}

/** Rezept (Vorlage/Grounding/Settings) je Channel anlegen/aktualisieren. */
export async function upsertRecipe(payload: {
  channel_id: string
  template_id?: string | null
  attribute_codes?: BulkAttributeCode[]
  google_search?: boolean
  settings?: Record<string, unknown>
}): Promise<{ success: boolean; recipe?: ChannelRecipe; error?: string }> {
  const res = await apiClient.put(`${BASE}/channel-content/recipes`, payload)
  return res.data
}

/** Rezept synchron an EINEM Beispielprodukt testen — persistiert nichts (Q4-Sample-Preview). */
export async function previewRecipe(payload: {
  channel_id: string
  product_id: string
  recipe?: Record<string, unknown>
  attribute_codes?: BulkAttributeCode[]
}): Promise<PreviewResult> {
  const res = await apiClient.post(`${BASE}/channel-content/recipes/preview`, payload)
  return res.data
}

/** Wizard-Vorschau: KI-Aufrufe × geschätzte Credits (kein Run wird angelegt). */
export async function estimateRun(payload: {
  product_ids: string[]
  channel_field_selections: ChannelFieldSelection[]
  research_enabled?: boolean
}): Promise<EstimateResult> {
  const res = await apiClient.post(`${BASE}/channel-content/runs/estimate`, payload)
  return res.data
}

/** Bulk-Lauf anlegen + dispatchen (202 bei Erfolg). */
export async function startRun(payload: {
  product_ids: string[]
  channel_field_selections: ChannelFieldSelection[]
  research_enabled?: boolean
}): Promise<{ success: boolean; run?: ChannelContentRun; error?: string }> {
  const res = await apiClient.post(`${BASE}/channel-content/runs`, payload)
  return res.data
}

/** Lauf-Liste (neueste zuerst, paginiert). */
export async function listRuns(
  page = 1,
): Promise<{ success: boolean; runs: ChannelContentRun[] }> {
  const res = await apiClient.get(`${BASE}/channel-content/runs${buildQuery({ page })}`)
  return res.data
}

/** Ein Run (Fortschritts-Polling). */
export async function getRun(
  runId: string,
): Promise<{ success: boolean; run: ChannelContentRun }> {
  const res = await apiClient.get(`${BASE}/channel-content/runs/${runId}`)
  return res.data
}

/** Review-Queue: Vorschläge mit Filtern (nur gesetzte Filter werden gesendet). */
export async function listSuggestions(
  filters: {
    run_id?: string
    route?: SuggestionRoute
    status?: SuggestionStatus
    channel_id?: string
    page?: number
  } = {},
): Promise<{ success: boolean; items: ChannelContentSuggestion[]; page: number }> {
  const qs = buildQuery({
    run_id: filters.run_id,
    route: filters.route,
    status: filters.status,
    channel_id: filters.channel_id,
    page: filters.page,
  })
  const res = await apiClient.get(`${BASE}/channel-content/suggestions${qs}`)
  return res.data
}

/** Vorschläge übernehmen (Einzel-/Bulk); edited_value nur bei genau einer id. */
export async function applySuggestions(payload: {
  suggestion_ids: string[]
  edited_value?: string | string[]
}): Promise<ApplyResult> {
  const res = await apiClient.post(`${BASE}/channel-content/suggestions/apply`, payload)
  return res.data
}

/** Vorschläge verwerfen, optional mit Grund (v2/Q5, fürs Rezept-Tuning). */
export async function discardSuggestions(payload: {
  suggestion_ids: string[]
  reason?: string
}): Promise<{ success: boolean; discarded: number }> {
  const res = await apiClient.post(`${BASE}/channel-content/suggestions/discard`, payload)
  return res.data
}

/** Fehlgeschlagene Paare eines Laufs erneut generieren (202; Worker ist paar-idempotent). */
export async function retryFailedPairs(
  runId: string,
): Promise<{ success: boolean; error?: string }> {
  const res = await apiClient.post(`${BASE}/channel-content/runs/${runId}/retry-failed`)
  return res.data
}

/** P2/T14: JTL-Push für übernommene Vorschläge mit fehlgeschlagenem Push wiederholen. */
export async function retryPushSuggestions(payload: {
  suggestion_ids: string[]
}): Promise<{ success: boolean; retried: number; pushed_ok: number; skipped: number; push_errors: PushError[] }> {
  const res = await apiClient.post(`${BASE}/channel-content/suggestions/push-retry`, payload)
  return res.data
}

/** P2/T13: previous_value erneut anwenden (Undo nach Freigabe, 1× je Vorschlag). */
export async function revertSuggestions(payload: {
  suggestion_ids: string[]
}): Promise<{ success: boolean; reverted: number; no_previous: number; skipped: number; push_errors: PushError[] }> {
  const res = await apiClient.post(`${BASE}/channel-content/suggestions/revert`, payload)
  return res.data
}

/** P2/T15: Batch-Lookup Produkt-id→Name für die Review-Anzeige (max. 100 ids). */
export async function getProductNames(ids: string[]): Promise<{ success: boolean; products: Record<string, string | null> }> {
  const res = await apiClient.get(`${BASE}/channel-content/product-names?ids=${ids.slice(0, 100).join(',')}`)
  return res.data
}

/** P1/B4: Badge-Zähler — offene „Braucht deinen Blick"-Vorschläge des Workspace. */
export async function getPendingSuggestionCount(): Promise<{ success: boolean; needs_review: number }> {
  const res = await apiClient.get(`${BASE}/channel-content/suggestions/pending-count`)
  return res.data
}
